import { useEffect, useState } from 'react'
import { useSearchParams, Link, useBlocker } from 'react-router-dom'
import { ArrowRight, Download, Upload, ArrowRightLeft } from 'lucide-react'
import { useCurrentUser, useShows } from '@/api/hooks'
import {
  downloadExport,
  TransferError,
  useInstallationIdentity,
  useTransferAction,
} from '@/api/show-transfer'
import { Button } from '@/components/ui/button'
import { Check, privacyWarning } from '@/components/show-transfer/files'
import {
  PrivacyOptions,
  StatusNotice,
  WorkflowFooter,
  WorkflowHeading,
  WorkflowLayout,
} from '@/components/show-transfer/WorkflowLayout'
import HandoverControls from '@/components/show-transfer/HandoverControls'
import DirectDelivery from '@/components/show-transfer/DirectDelivery'

export default function ShowTransferPage() {
  const user = useCurrentUser()
  if (user.isLoading) return <p role="status">Loading administrator access…</p>
  if (!user.data?.isAdmin)
    return <StatusNotice tone="error">Administrator access is required.</StatusNotice>
  return <TransferWorkspace key={user.data.id} />
}
function TransferWorkspace() {
  const [params, setParams] = useSearchParams()
  const shows = useShows(),
    identity = useInstallationIdentity()
  const showId = params.get('showId') ?? ''
  const [handoverLocked, setHandoverLocked] = useState(false),
    [deliveryLocked, setDeliveryLocked] = useState(false)
  const [workflowGeneration, setWorkflowGeneration] = useState(0)
  const [profile, setProfile] = useState<'limited' | 'full-contact'>('limited')
  const [acknowledged, setAcknowledged] = useState(false)
  const [role, setRole] = useState<'source' | 'destination'>('source')
  const [moveConfigured, setMoveConfigured] = useState(false)
  const [recovery, setRecovery] = useState<'import' | 'file' | 'delivery'>('import')
  const requestedTask = params.get('task') ?? 'home'
  const task = ['home', 'export', 'move', 'received', 'recover'].includes(requestedTask)
      ? requestedTask
      : 'home',
    method = params.get('method') === 'paired' ? 'paired' : 'file'
  const exportAction = useTransferAction(() => downloadExport(showId, profile))
  const locked = handoverLocked || deliveryLocked || exportAction.isPending
  const blocker = useBlocker(locked)
  useEffect(() => {
    const leave = (event: BeforeUnloadEvent) => {
      if (locked) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', leave)
    return () => window.removeEventListener('beforeunload', leave)
  }, [locked])
  function change(key: string, value: string) {
    if (locked) return
    setParams(previous => {
      const next = new URLSearchParams(previous)
      next.set(key, value)
      return next
    })
  }
  const home = `/admin/show-transfer${showId ? `?showId=${encodeURIComponent(showId)}` : ''}`
  const titles: Record<string, [string, string]> = {
    home: ['Import & export', 'Bring in a show, save a copy, or move where it’s run.'],
    export: ['Export a copy', 'Download or send a show file. You can keep running the show here.'],
    move: ['Move a show', 'Transfer editing to another server, with both administrators.'],
    received: ['Import a show', 'Review shows received from a connected server before importing.'],
    recover: [
      'Resume an operation',
      'Recover an existing import or transfer before starting another.',
    ],
  }
  const [title, description] = titles[task] ?? titles.home
  const methodPicker = (
    <fieldset disabled={locked} className="flex flex-wrap gap-4 text-sm">
      <legend className="mb-2 font-semibold">Transfer method</legend>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name="method"
          checked={method === 'file'}
          onChange={() => change('method', 'file')}
        />
        Download / upload files
      </label>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name="method"
          checked={method === 'paired'}
          onChange={() => change('method', 'paired')}
        />
        Connected server
      </label>
    </fieldset>
  )
  const showPicker = (
    <div className="space-y-2">
      <label className="text-sm font-medium" htmlFor="transfer-show">
        Show
      </label>
      <select
        id="transfer-show"
        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
        value={showId}
        disabled={locked}
        onChange={e => {
          change('showId', e.target.value)
          setAcknowledged(false)
        }}
      >
        <option value="">Select a show</option>
        {shows.data?.map(show => {
          const sameName = shows.data.filter(other => other.name === show.name)
          const context = [show.startDate?.slice(0, 10), show.location, show.status]
            .filter(Boolean)
            .join(' · ')
          const ambiguous = sameName.some(
            other =>
              other.id !== show.id &&
              [other.startDate?.slice(0, 10), other.location, other.status]
                .filter(Boolean)
                .join(' · ') === context
          )
          const detail =
            context && !ambiguous ? context : [context, show.id].filter(Boolean).join(' · ')
          return (
            <option key={show.id} value={show.id}>
              {show.name}
              {sameName.length > 1 ? ` — ${detail}` : ''}
            </option>
          )
        })}
      </select>
      {shows.isError && (
        <StatusNotice tone="error">
          Unable to load shows.{' '}
          <Button variant="outline" onClick={() => void shows.refetch()}>
            Try again
          </Button>
        </StatusNotice>
      )}
    </div>
  )
  return (
    <WorkflowLayout
      title={title}
      description={description}
      backTo={task === 'home' ? '/shows' : home}
      backLabel={task === 'home' ? 'Shows' : 'Import & export'}
    >
      {blocker.state === 'blocked' && (
        <StatusNotice tone="warning" role="alert" title="Keep this operation open">
          <p>
            An operation is in progress or its outcome is unknown. Stay here to recover the result
            and save its recovery file before leaving. Pending intake is not cleared.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => blocker.reset()}>Stay and recover</Button>
            <Button
              variant="destructive"
              onClick={() => {
                setWorkflowGeneration(current => current + 1)
                setHandoverLocked(false)
                setDeliveryLocked(false)
                setMoveConfigured(false)
                exportAction.reset()
                blocker.proceed()
              }}
            >
              Leave without saved recovery
            </Button>
          </div>
        </StatusNotice>
      )}
      {task === 'home' && (
        <>
          <div className="divide-y border-y">
            {[
              {
                label: 'Import a show',
                detail: 'Bring in a Honey Show file. Review changes before saving.',
                Icon: Download,
                to: `/admin/show-import${showId ? `?showId=${encodeURIComponent(showId)}` : ''}`,
              },
              {
                label: 'Export a copy',
                detail: 'Download a show to keep or share. The show stays here.',
                Icon: Upload,
                to: `${home}${showId ? '&' : '?'}task=export`,
              },
              {
                label: 'Move a show',
                detail: 'Transfer editing to another server, with both administrators.',
                Icon: ArrowRightLeft,
                to: `${home}${showId ? '&' : '?'}task=move`,
              },
            ].map(({ label, detail, Icon, to }) => (
              <Link key={label} to={to} className="flex items-center gap-4 py-6 hover:bg-muted/50">
                <Icon className="size-5 shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{label}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{detail}</p>
                </div>
                <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <WorkflowFooter
            secondary={<span className="text-sm text-muted-foreground">Already started?</span>}
          >
            <Button variant="link" onClick={() => change('task', 'recover')}>
              Resume an import or transfer
            </Button>
          </WorkflowFooter>
        </>
      )}
      {task === 'export' && (
        <div className="space-y-6">
          <WorkflowHeading>Choose what to export</WorkflowHeading>
          {showPicker}
          {methodPicker}
          {method === 'file' && (
            <>
              <PrivacyOptions
                value={profile}
                disabled={locked}
                onChange={value => {
                  setProfile(value)
                  setAcknowledged(false)
                }}
              />
              <StatusNotice tone="warning" title="Share this file carefully">
                <p>{privacyWarning}</p>
                {profile === 'full-contact' && (
                  <Check checked={acknowledged} onChange={setAcknowledged} disabled={locked}>
                    I understand this file includes unencrypted contact details.
                  </Check>
                )}
              </StatusNotice>
              {exportAction.isError && (
                <StatusNotice tone="error" title="Export failed">
                  <p>{exportFailureMessage(exportAction.error)}</p>
                  {exportAction.error instanceof TransferError && (
                    <details className="mt-3">
                      <summary className="cursor-pointer">Technical details</summary>
                      <p className="mt-2 break-words">
                        {exportAction.error.code} (HTTP {exportAction.error.status})
                      </p>
                      {exportAction.error.diagnostics.map((item, index) => (
                        <p key={index} className="mt-2 break-words">
                          {item.path}: {item.message}
                        </p>
                      ))}
                    </details>
                  )}
                </StatusNotice>
              )}
              {exportAction.isSuccess && (
                <StatusNotice tone="success" role="status" title="Show file downloaded">
                  Editing remains on this server.
                </StatusNotice>
              )}
              <WorkflowFooter
                secondary={
                  <Button variant="outline" asChild>
                    <Link to={home}>Cancel</Link>
                  </Button>
                }
              >
                <Button
                  disabled={!showId || locked || (profile === 'full-contact' && !acknowledged)}
                  onClick={() => exportAction.mutate()}
                >
                  {exportAction.isPending ? 'Preparing file…' : 'Download show file'}
                </Button>
              </WorkflowFooter>
            </>
          )}
        </div>
      )}
      {task === 'move' && !moveConfigured && (
        <div className="space-y-6">
          <WorkflowHeading>Set up the transfer</WorkflowHeading>
          <fieldset disabled={locked} className="space-y-3">
            <legend className="mb-2 text-sm font-semibold">
              What are you doing on this server?
            </legend>
            {(['source', 'destination'] as const).map(value => (
              <label
                key={value}
                className="flex items-start gap-3 rounded-lg border p-4 has-[:checked]:border-primary has-[:checked]:bg-muted/50"
              >
                <input
                  type="radio"
                  name="server-role"
                  className="mt-1"
                  checked={role === value}
                  onChange={() => setRole(value)}
                />
                <span className="text-sm">
                  <span className="block font-medium">
                    {value === 'source' ? 'Sending the show' : 'Receiving the show'}
                  </span>
                  <span className="text-muted-foreground">
                    {value === 'source'
                      ? 'This is where the show is currently being run.'
                      : 'This is where the show will be run next.'}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          {role === 'source' && showPicker}
          {methodPicker}
          <StatusNotice tone="warning" title="Coordinate with the other administrator">
            Only one server can run the show at a time. Starting setup does not pause editing.
          </StatusNotice>
          <WorkflowFooter
            secondary={
              <Button variant="outline" asChild>
                <Link to={home}>Cancel</Link>
              </Button>
            }
          >
            <Button
              disabled={locked || (role === 'source' && !showId)}
              onClick={() => setMoveConfigured(true)}
            >
              Continue
            </Button>
          </WorkflowFooter>
        </div>
      )}
      {task === 'move' && moveConfigured && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/50 p-4 text-sm">
          <span>
            {role === 'source' ? 'Sending' : 'Receiving'} ·{' '}
            {method === 'file' ? 'Using files' : 'Connected server'}
            {role === 'source' && shows.data?.find(show => show.id === showId)?.name
              ? ` · ${shows.data.find(show => show.id === showId)!.name}`
              : ''}
          </span>
          <Button variant="link" disabled={locked} onClick={() => setMoveConfigured(false)}>
            Change setup
          </Button>
        </div>
      )}
      {task === 'recover' && (
        <div className="space-y-6">
          <WorkflowHeading>Choose what to recover</WorkflowHeading>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Operation</span>
            <select
              className="h-10 w-full rounded-md border bg-background px-3"
              disabled={locked}
              value={recovery}
              onChange={e => setRecovery(e.target.value as typeof recovery)}
            >
              <option value="import">Import from a file</option>
              <option value="file">Transfer using files</option>
              <option value="delivery">Connected-server delivery</option>
            </select>
          </label>
          {recovery === 'import' && (
            <>
              <StatusNotice tone="warning" title="Resume the existing import">
                Choose the original show file, then load your saved review recovery file. Don’t
                start a new import while its outcome is unknown.
              </StatusNotice>
              <WorkflowFooter>
                <Button asChild>
                  <Link
                    to={`/admin/show-import?recover=1${showId ? `&showId=${encodeURIComponent(showId)}` : ''}`}
                  >
                    Resume import
                  </Link>
                </Button>
              </WorkflowFooter>
            </>
          )}
        </div>
      )}
      <div
        hidden={
          !(
            (task === 'move' && moveConfigured && method === 'file') ||
            (task === 'recover' && recovery === 'file')
          )
        }
      >
        <HandoverControls
          key={`${showId}:${workflowGeneration}`}
          showId={showId}
          disabled={deliveryLocked || exportAction.isPending}
          onLocked={setHandoverLocked}
          guided
          role={role}
          recoveryOnly={task === 'recover'}
        />
      </div>
      {identity.data && (
        <div
          hidden={
            !(
              (task === 'export' && method === 'paired') ||
              (task === 'move' && moveConfigured && method === 'paired') ||
              task === 'received' ||
              (task === 'recover' && recovery === 'delivery')
            )
          }
        >
          <DirectDelivery
            key={workflowGeneration}
            showId={
              task === 'received' || (task === 'move' && role === 'destination') ? '' : showId
            }
            installationKey={identity.data.installationKey}
            disabled={handoverLocked || exportAction.isPending}
            onLocked={setDeliveryLocked}
            view={
              task === 'received' || (task === 'move' && role === 'destination')
                ? 'inbox'
                : task === 'recover'
                  ? 'recover'
                  : task === 'move'
                    ? 'send-move'
                    : 'send-copy'
            }
          />
        </div>
      )}
      {identity.isError && task !== 'home' && (
        <StatusNotice tone="error">
          Unable to load this server’s identity.{' '}
          <Button variant="outline" onClick={() => void identity.refetch()}>
            Try again
          </Button>
        </StatusNotice>
      )}
    </WorkflowLayout>
  )
}

function exportFailureMessage(error: Error | null) {
  if (error instanceof TransferError) {
    if (error.status === 401) return 'Your session has expired. Sign in again to export this show.'
    if (error.status === 403)
      return 'Your account does not have administrator access to export shows.'
    if (error.code === 'SHOW_NOT_FOUND')
      return 'This show is no longer available. Choose another show.'
    if (error.code === 'SHOW_PACKAGE_INVALID_SOURCE')
      return 'This show has data that cannot be included in a valid show file. Review the technical details before trying again.'
    if (error.code === 'SHOW_PACKAGE_IDENTITY_CONFLICT')
      return 'This show has conflicting transfer identities. These need to be reviewed before it can be exported.'
    if (error.status === 503)
      return 'The server is busy. Wait a moment, then try downloading again.'
  }
  return 'The show file could not be prepared. Try again. If this continues, contact the server administrator.'
}
